import type { BrandDiscountMap } from "@/lib/groupBrandDiscount";

export type ResellerGroup = {
  id: string;
  slot: number;
  name: string;
  discountPersen: number;
  isActive: boolean;
  /**
   * Diskon per merek level ini (mig 7070) — nama merek ternormalisasi → persen,
   * diisi pemuat yang membaca `group_brand_discount_rules()`. Absen = belum
   * dimuat / level tanpa aturan. MENTAH: pricing.ts tak membacanya langsung —
   * selesaikan dulu per produk lewat `levelForProduct` (groupBrandDiscount.ts).
   */
  brandDiscounts?: BrandDiscountMap;
  /**
   * Aturan merek yang SUDAH diselesaikan untuk SATU produk (`levelForProduct`).
   * Hanya field ini yang dibaca tangga harga: ada → menggantikan override
   * per-varian slot ini DAN diskon % rata level (keputusan pemilik 2026-10-08).
   */
  brandPersen?: number | null;
};

/** Group null = Customer biasa, tidak dapat diskon */
export type CustomerGroup = ResellerGroup | null;

/**
 * Hitung harga setelah diskon grup.
 * Jika group null atau discountPersen = 0, kembalikan price_normal.
 */
export function applyGroupDiscount(
  priceNormal: number | undefined,
  group: CustomerGroup
): number | undefined {
  if (priceNormal === undefined) return undefined;
  if (!group || group.discountPersen <= 0) return priceNormal;
  return Math.round(priceNormal * (1 - group.discountPersen / 100));
}

export function formatGroupBadge(group: CustomerGroup): string | null {
  if (!group) return null;
  return `Harga ${group.name}`;
}

/**
 * Plafon slot grup per toko (mig 5600: 4 → 8; mig 7300: 8 → 15).
 *
 * Angkanya BUKAN sekadar konstanta UI: ia dicerminkan constraint DB
 * `reseller_groups_slot_check` dan `promo_campaigns_level_slots_check`. Menaikkan
 * yang satu tanpa yang lain membuat grup baru lolos di layar lalu ditolak DB —
 * atau lebih buruk, kampanye promo ber-slot tinggi tersimpan diam-diam sebagai
 * "semua pembeli". Ubah keduanya bersama, lewat migrasi baru.
 */
export const MAX_GROUP_SLOT = 15;

/**
 * Slot grup → kolom diskon per-varian. SATU sumber untuk EMPAT pembaca yang
 * wajib sepakat: `getVariantPrice` (pricing.ts) yang menghitung harga pembeli,
 * prefill nota (invoices.ts), export PO reseller (poDetail.ts), dan pratinjau
 * form produk (variantPricing.ts).
 *
 * KENAPA DISATUKAN. Sebelum mig 5600 keempatnya memegang salinan `switch`
 * sendiri-sendiri. Salinan yang tertinggal satu slot tidak melempar error apa
 * pun — ia diam-diam jatuh ke diskon grup rata, jadi satu layar menampilkan
 * harga yang berbeda dari yang ditagihkan. Itu kelas bug #2095. Dengan satu peta,
 * menambah slot berikutnya adalah satu baris, bukan empat yang harus diingat.
 *
 * Dua bentuk karena dua dunia: `KEY` untuk objek `ProductVariation` yang sudah
 * di-map ke camelCase, `COLUMN` untuk query PostgREST mentah.
 */
export const DISKON_SLOT_KEY = {
  1: "diskonReseller",
  2: "diskonAgen",
  3: "diskonSlot3",
  4: "diskonSlot4",
  5: "diskonSlot5",
  6: "diskonSlot6",
  7: "diskonSlot7",
  8: "diskonSlot8",
  9: "diskonSlot9",
  10: "diskonSlot10",
  11: "diskonSlot11",
  12: "diskonSlot12",
  13: "diskonSlot13",
  14: "diskonSlot14",
  15: "diskonSlot15"
} as const;

export const DISKON_SLOT_COLUMN = {
  1: "diskon_reseller",
  2: "diskon_agen",
  3: "diskon_slot3",
  4: "diskon_slot4",
  5: "diskon_slot5",
  6: "diskon_slot6",
  7: "diskon_slot7",
  8: "diskon_slot8",
  9: "diskon_slot9",
  10: "diskon_slot10",
  11: "diskon_slot11",
  12: "diskon_slot12",
  13: "diskon_slot13",
  14: "diskon_slot14",
  15: "diskon_slot15"
} as const;

/** Slot yang punya kolom diskon per-varian — sumber tunggal untuk perulangan. */
export const DISKON_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const;

export type DiskonSlot = (typeof DISKON_SLOTS)[number];

/** `true` bila slot punya kolom diskon per-varian (bukan sekadar angka > 0). */
export function isDiskonSlot(slot: number): slot is DiskonSlot {
  return Number.isInteger(slot) && slot >= 1 && slot <= MAX_GROUP_SLOT;
}

/** Nama kolom DB diskon untuk slot ini, atau `null` bila slot di luar plafon. */
export function diskonColumnForSlot(slot: number): string | null {
  return isDiskonSlot(slot) ? DISKON_SLOT_COLUMN[slot] : null;
}
